import { DeviceLocationFix } from '@/utils/captureBestDeviceLocation';
import { GpsCaptureSource, GeolocationPermissionState } from '@/types/LocationVerification.type';

export type PhotoGpsCaptureSession = {
  clientEvidenceId: string;
  gpsPromise: Promise<DeviceLocationFix | null>;
};

export type PhotoEvidenceLocationFields = {
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  altitude: number | null;
  altitudeAccuracy: number | null;
  heading: number | null;
  speed: number | null;
  gpsCaptureSource: GpsCaptureSource | null;
  gpsAccuracyPreciseEnough: boolean | null;
  connectionType: string | null;
  connectionEffectiveType: string | null;
  geolocationPermissionState: GeolocationPermissionState | null;
  clientUserAgent: string | null;
  gpsCapturedAt: string;
};

export class PhotoGpsRequiredError extends Error {
  readonly name = 'PhotoGpsRequiredError';

  constructor() {
    super('Photo GPS coordinates are required');
  }
}

export function photoEvidenceLocationFieldsFromFix(
  location: DeviceLocationFix | null,
  fallbackCapturedAt: string
): PhotoEvidenceLocationFields {
  return {
    latitude: location?.latitude ?? null,
    longitude: location?.longitude ?? null,
    accuracy: location?.accuracyMetres ?? null,
    altitude: location?.altitude ?? null,
    altitudeAccuracy: location?.altitudeAccuracy ?? null,
    heading: location?.heading ?? null,
    speed: location?.speed ?? null,
    gpsCaptureSource: location?.gpsCaptureSource ?? null,
    gpsAccuracyPreciseEnough: location?.gpsAccuracyPreciseEnough ?? null,
    connectionType: location?.connectionType ?? null,
    connectionEffectiveType: location?.connectionEffectiveType ?? null,
    geolocationPermissionState: location?.geolocationPermissionState ?? null,
    clientUserAgent: location?.clientUserAgent ?? null,
    gpsCapturedAt: location?.gpsCapturedAt ?? fallbackCapturedAt,
  };
}

export function photoEvidenceLocationHasCoordinates(fields: PhotoEvidenceLocationFields): boolean {
  return fields.latitude != null && fields.longitude != null;
}

export function assertPhotoEvidenceHasGps(fields: PhotoEvidenceLocationFields): void {
  if (!photoEvidenceLocationHasCoordinates(fields)) {
    throw new PhotoGpsRequiredError();
  }
}

export function isPhotoGpsRequiredError(error: unknown): error is PhotoGpsRequiredError {
  return error instanceof PhotoGpsRequiredError;
}

export function startPhotoGpsCapture(params: {
  generateId: () => string;
  captureGps: () => Promise<DeviceLocationFix | null>;
}): PhotoGpsCaptureSession {
  return {
    clientEvidenceId: params.generateId(),
    gpsPromise: params.captureGps(),
  };
}

/**
 * Resolves GPS fields for evidence using the pre-started camera-session fix, with optional fallback.
 */
export async function resolvePhotoEvidenceLocation(params: {
  photoCapturedAt: string;
  gpsPromise?: Promise<DeviceLocationFix | null> | null;
  fallbackCapture?: () => Promise<DeviceLocationFix | null>;
}): Promise<PhotoEvidenceLocationFields> {
  let location: DeviceLocationFix | null = null;
  if (params.gpsPromise) {
    location = await params.gpsPromise;
  }
  if (!location && params.fallbackCapture) {
    location = await params.fallbackCapture();
  }
  return photoEvidenceLocationFieldsFromFix(location, params.photoCapturedAt);
}
